export type AudioSourceType = "file" | "system" | "microphone";
export interface AudioSource {
  type: AudioSourceType;
  file?: File;
  stream?: MediaStream;
  label: string;
  enableLookAhead?: boolean;
  /** Created and resumed in the Enter performance tap; owned by App, not effect cleanup. */
  audioContext?: AudioContext;
}

/** A single graph and transport; capture tracks and handed-in contexts belong to the caller. */
export class AudioSession {
  private analyser: AnalyserNode;
  private delay: DelayNode;
  private output: GainNode;
  private frequency: Float32Array<ArrayBuffer>;
  private waveform: Float32Array<ArrayBuffer>;
  private buffer: AudioBuffer | null = null;
  private node: AudioBufferSourceNode | null = null;
  private streamNode: MediaStreamAudioSourceNode | null = null;
  private disposed = false;
  private offset = 0;
  private startedAt = 0;
  private requested = false;
  private command = 0;
  private tracks: MediaStreamTrack[] = [];
  loading = true;
  error: string | null = null;

  constructor(readonly context: AudioContext, readonly source: AudioSource, fftSize: number,
    lookAhead: number, private changed: () => void) {
    this.analyser = context.createAnalyser();
    this.analyser.fftSize = fftSize;
    this.analyser.smoothingTimeConstant = 0;
    this.frequency = new Float32Array(this.analyser.frequencyBinCount);
    this.waveform = new Float32Array(fftSize);
    this.delay = context.createDelay(1);
    this.output = context.createGain();
    // Keep a pull path for Safari while never monitoring a microphone through speakers.
    this.output.gain.value = source.type === "file" || (source.type === "system" && source.enableLookAhead) ? 1 : 0;
    this.analyser.connect(this.delay);
    this.delay.connect(this.output);
    this.output.connect(context.destination);
    this.setLookAhead(lookAhead);
    context.addEventListener("statechange", this.notify);
  }

  private notify = () => { if (!this.disposed) this.changed(); };
  private ended = () => {
    if (this.tracks.every(track => track.readyState === "ended")) {
      this.requested = false;
      this.error = "音源已断开，请更换音源后重新连接。";
    }
    this.notify();
  };

  async load() {
    try {
      if (this.source.type === "file") {
        if (!this.source.file) throw new Error("请选择音频文件。");
        const bytes = await this.source.file.arrayBuffer();
        if (this.disposed) return;
        const buffer = await this.context.decodeAudioData(bytes);
        if (this.disposed) return;
        this.buffer = buffer;
      } else {
        const stream = this.source.stream;
        if (!stream) throw new Error("请重新连接音源。");
        this.tracks = stream.getAudioTracks();
        if (!this.tracks.some(track => track.readyState === "live")) throw new Error("音源已断开，请重新连接。");
        this.streamNode = this.context.createMediaStreamSource(stream);
        this.streamNode.connect(this.analyser);
        for (const track of this.tracks) {
          track.addEventListener("ended", this.ended);
          track.addEventListener("mute", this.notify);
          track.addEventListener("unmute", this.notify);
        }
        this.requested = true;
      }
    } catch (error) {
      if (this.disposed) return;
      this.error = error instanceof Error ? error.message : "无法读取音频，请换用 MP3、WAV 或 M4A。";
    } finally {
      if (!this.disposed) { this.loading = false; this.notify(); }
    }
  }

  get isPlaying() {
    return !this.disposed && this.requested && this.context.state === "running" &&
      (this.source.type === "file" || this.tracks.some(track => track.readyState === "live" && !track.muted && track.enabled));
  }
  get needsResume() {
    return !this.disposed && !this.error && this.context.state !== "running";
  }
  get inputMuted() { return this.source.type !== "file" && this.requested && this.tracks.every(track => track.muted); }

  async resume() {
    if (this.disposed) return;
    try {
      // Includes Safari's interrupted state, not only suspended.
      if (this.context.state !== "running") await this.context.resume();
    } catch { /* A visible resume action remains available if user activation is still needed. */ }
    this.notify();
  }

  async play() {
    if (this.disposed || this.loading || this.error) return;
    const command = ++this.command;
    await this.resume();
    if (this.disposed || command !== this.command || this.context.state !== "running") return;
    if (this.source.type !== "file") { this.requested = true; this.notify(); return; }
    if (!this.buffer) return;
    // An interrupted source already retains its position on the AudioContext clock.
    if (this.node && this.requested) { this.notify(); return; }
    this.releaseNode();
    if (this.offset >= this.buffer.duration) this.offset = 0;
    const node = this.context.createBufferSource();
    node.buffer = this.buffer;
    node.connect(this.analyser);
    this.node = node;
    this.startedAt = this.context.currentTime - this.offset;
    this.requested = true;
    node.onended = () => {
      if (this.disposed || this.node !== node) return;
      this.node = null; node.disconnect(); this.requested = false; this.offset = 0; this.notify();
    };
    node.start(0, this.offset);
    this.notify();
  }

  private releaseNode() {
    if (!this.node) return;
    this.node.onended = null;
    try { this.node.stop(); } catch { /* Already ended. */ }
    this.node.disconnect(); this.node = null;
  }
  pause() {
    this.command++;
    this.offset = this.getCurrentTime();
    this.requested = false;
    this.releaseNode(); this.notify();
  }
  stop() { this.pause(); this.offset = 0; this.notify(); }
  seek(time: number) {
    if (!Number.isFinite(time) || !this.buffer) return;
    this.pause(); this.offset = Math.max(0, Math.min(time, this.buffer.duration));
    void this.play();
  }
  getCurrentTime() {
    if (!this.buffer) return 0;
    return this.node && this.requested ? Math.min(this.buffer.duration, Math.max(0, this.context.currentTime - this.startedAt)) : this.offset;
  }
  getDuration() { return this.buffer?.duration ?? 0; }
  setLookAhead(ms: number) {
    const supported = this.source.type === "file" || (this.source.type === "system" && this.source.enableLookAhead);
    this.delay.delayTime.value = supported && Number.isFinite(ms) ? Math.max(0, Math.min(1000, ms)) / 1000 : 0;
  }
  getFrequencyData() {
    if (this.isPlaying) this.analyser.getFloatFrequencyData(this.frequency); else this.frequency.fill(-Infinity);
    return this.frequency;
  }
  getTimeDomainData() {
    if (this.isPlaying) this.analyser.getFloatTimeDomainData(this.waveform); else this.waveform.fill(0);
    return this.waveform;
  }
  stopCapture() { this.source.stream?.getTracks().forEach(track => track.stop()); this.ended(); }
  dispose() {
    this.disposed = true; this.command++;
    this.context.removeEventListener("statechange", this.notify);
    for (const track of this.tracks) {
      track.removeEventListener("ended", this.ended);
      track.removeEventListener("mute", this.notify);
      track.removeEventListener("unmute", this.notify);
    }
    this.releaseNode(); this.streamNode?.disconnect();
    this.analyser.disconnect(); this.delay.disconnect(); this.output.disconnect();
    this.buffer = null;
  }
}
